// Low-latency audio for the phonics blender.
//
// For each sound key we keep the best playable source:
//   - a decoded AudioBuffer (lowest latency; used for bundled sounds and any
//     remote sound whose host allows cross-origin fetch), and/or
//   - an <audio> element (fallback for remote/Drive sounds that can't be fetched
//     for Web Audio but can still stream in a media element).
// Preference at play time: the real (remote) recording first, bundled last, so
// there is always something to hear.
//
// The AudioContext starts "suspended" until a user gesture. The FIRST sound is
// deferred until resume() actually completes, so it isn't clipped/dropped.

import { BLEND } from './config.js'

// Vowels are the long, sustained part of a syllable; consonants are short, and
// stop consonants (p/t/k/b/d/g) are the shortest (a quick burst). The blend
// shapes durations accordingly so it sounds like a word, not equal beeps.
const STOPS = new Set(['p', 't', 'k', 'b', 'd', 'g'])
const VOWELS = new Set(['a', 'e', 'i', 'o', 'u', 'ae', 'ee', 'ie', 'oe', 'ue', 'oo', 'uu', 'ow', 'oi', 'ar', 'or', 'ur', 'air', 'ear', 'uh'])

let ctx = null
let masterGain = null
let unlocked = false
let warmed = false
const entries = new Map() // key -> { buffer, remote, media }
const overrides = new Map() // key -> { buffer, trim }  user recordings take priority

export function initAudio() {
  if (ctx) return ctx
  const AC = window.AudioContext || window.webkitAudioContext
  ctx = new AC()
  masterGain = ctx.createGain()
  masterGain.gain.value = 1
  masterGain.connect(ctx.destination)
  return ctx
}

// Run inside the first user gesture (also wired to the earliest touch below).
export function unlockAudio() {
  initAudio()
  if (ctx.state === 'suspended') ctx.resume()
  if (!unlocked) {
    const b = ctx.createBuffer(1, 1, 22050)
    const s = ctx.createBufferSource()
    s.buffer = b
    s.connect(masterGain)
    s.start(0)
    unlocked = true
  }
}

function decode(arrayBuffer) {
  return new Promise((resolve, reject) => {
    const p = ctx.decodeAudioData(arrayBuffer, resolve, reject)
    if (p && typeof p.then === 'function') p.then(resolve, reject)
  })
}

// Find the voiced span of a buffer (skip leading/trailing silence) so the blend
// can play just the sound, not the gaps around it. Returns { start, dur } in sec.
function computeTrim(buffer, thr) {
  try {
    const ch = buffer.getChannelData(0)
    const n = ch.length, sr = buffer.sampleRate
    let s = 0, e = n - 1
    while (s < n && Math.abs(ch[s]) < thr) s++
    while (e > s && Math.abs(ch[e]) < thr) e--
    if (e <= s) return { start: 0, dur: buffer.duration }
    const pad = Math.round(0.012 * sr) // keep a hair of onset/offset
    s = Math.max(0, s - pad); e = Math.min(n - 1, e + pad)
    return { start: s / sr, dur: (e - s) / sr }
  } catch (_) {
    return { start: 0, dur: buffer.duration }
  }
}

async function tryDecode(url) {
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const arr = await res.arrayBuffer()
    return await decode(arr)
  } catch (_) {
    return null
  }
}

// manifest: { key -> { remote?, bundled? } }. Loads all in parallel; tolerant of
// individual failures. Prefers decoding the remote sound; keeps an <audio>
// element for remote as a fallback; always tries the bundled sound too.
export async function loadAudioManifest(manifest) {
  initAudio()
  await Promise.all(
    Object.entries(manifest).map(async ([key, spec]) => {
      const e = { buffer: null, remote: false, media: null }
      const remotes = spec.remote ? (Array.isArray(spec.remote) ? spec.remote : [spec.remote]) : []
      // Find the first candidate that is a REAL audio file (skip 404s and the
      // dev-server's HTML fallback), then decode it — and keep it for the media
      // element so it still plays even if Web Audio can't decode it (e.g. .mp4).
      let realUrl = null
      for (const url of remotes) {
        try {
          const res = await fetch(url)
          if (!res.ok) continue
          const ct = (res.headers.get('content-type') || '').toLowerCase()
          if (ct.includes('text/html')) continue // missing file served as index.html
          realUrl = url
          const arr = await res.arrayBuffer()
          let b = null
          try { b = await decode(arr) } catch (_) { b = null }
          if (b) { e.buffer = b; e.remote = true; e.trim = computeTrim(b, BLEND.silence) }
          break
        } catch (_) { /* try next candidate */ }
      }
      if (realUrl) {
        try { e.media = new Audio(); e.media.preload = 'auto'; e.media.src = realUrl } catch (_) { e.media = null }
      }
      if (!e.buffer && spec.bundled) {
        const b = await tryDecode(spec.bundled)
        if (b) { e.buffer = b; e.remote = false; e.trim = computeTrim(b, BLEND.silence) }
      }
      entries.set(key, e)
    })
  )
  return entries.size
}

export function isLoaded(key) {
  const e = entries.get(key)
  return !!(e && (e.buffer || (e.media && e.media.readyState >= 2)))
}

// ---- user-recording overrides ----
// A decoded recording registered under a key wins over the library sound in both
// play() and playSequence(). Used to make recorded phonemes/words the default.
export async function decodeBlob(blob) {
  initAudio()
  const arr = await blob.arrayBuffer()
  return await decode(arr)
}
export function setOverride(key, buffer) {
  overrides.set(key, { buffer, trim: computeTrim(buffer, BLEND.silence) })
}
export function clearOverride(key) { overrides.delete(key) }
export function hasOverride(key) { return overrides.has(key) }
export function overrideDuration(key) { const o = overrides.get(key); return o ? o.buffer.duration : 0 }

// Decode a recorded blob, cut the leading/trailing silence, and re-encode it as a
// tight WAV. Returns { blob, mime, dur } or null if it can't be decoded.
function encodeWav(buffer) {
  const numCh = buffer.numberOfChannels, sr = buffer.sampleRate, len = buffer.length
  const blockAlign = numCh * 2, dataSize = len * blockAlign
  const out = new ArrayBuffer(44 + dataSize)
  const view = new DataView(out)
  const str = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)) }
  str(0, 'RIFF'); view.setUint32(4, 36 + dataSize, true); str(8, 'WAVE')
  str(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true)
  view.setUint16(22, numCh, true); view.setUint32(24, sr, true)
  view.setUint32(28, sr * blockAlign, true); view.setUint16(32, blockAlign, true); view.setUint16(34, 16, true)
  str(36, 'data'); view.setUint32(40, dataSize, true)
  const chans = []
  for (let c = 0; c < numCh; c++) chans.push(buffer.getChannelData(c))
  let off = 44
  for (let i = 0; i < len; i++) {
    for (let c = 0; c < numCh; c++) {
      let s = Math.max(-1, Math.min(1, chans[c][i]))
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true)
      off += 2
    }
  }
  return new Blob([out], { type: 'audio/wav' })
}
export async function trimmedWavFromBlob(blob) {
  initAudio()
  try {
    const buffer = await decode(await blob.arrayBuffer())
    const { start, dur } = computeTrim(buffer, BLEND.silence)
    const sr = buffer.sampleRate
    const startSample = Math.max(0, Math.floor(start * sr))
    const frames = Math.max(1, Math.min(buffer.length - startSample, Math.round(dur * sr)))
    const out = ctx.createBuffer(buffer.numberOfChannels, frames, sr)
    for (let c = 0; c < buffer.numberOfChannels; c++) {
      const src = buffer.getChannelData(c), dst = out.getChannelData(c)
      for (let i = 0; i < frames; i++) dst[i] = src[startSample + i] || 0
    }
    return { blob: encodeWav(out), mime: 'audio/wav', dur: frames / sr }
  } catch (_) {
    return null // undecodable → caller keeps the original take
  }
}

function startBuffer(buf, gain) {
  const src = ctx.createBufferSource()
  src.buffer = buf
  if (gain && gain !== 1) {
    const g = ctx.createGain()
    g.gain.value = gain
    src.connect(g).connect(masterGain)
  } else {
    src.connect(masterGain)
  }
  src.start(ctx.currentTime)
  return src
}

function playMedia(el) {
  // Play the pre-buffered element itself — its readiness is what play() gated on.
  // (A fresh clone would start at readyState 0 and re-fetch, adding latency or
  // silence.) The blender plays distinct letters, so restart-on-replay is fine.
  try {
    el.currentTime = 0
    const p = el.play()
    if (p && p.catch) p.catch(() => {})
    return el
  } catch (_) {
    return null
  }
}

// Fire-and-forget. User recording (override) first, then the real library
// recording, then the bundled placeholder.
export function play(key, { gain = 1 } = {}) {
  if (!ctx) return null
  const e = entries.get(key)
  const ov = overrides.get(key)
  if (!e && !ov) return null

  const doPlay = () => {
    if (ov) return startBuffer(ov.buffer, gain)                      // user recording (highest priority)
    if (e.buffer && e.remote) return startBuffer(e.buffer, gain)      // real recording, decoded (fast)
    if (e.media && e.media.readyState >= 2) return playMedia(e.media) // real recording via <audio>, only if it actually loaded
    if (e.buffer) return startBuffer(e.buffer, gain)                  // bundled placeholder (last resort)
    return null
  }

  // Make sure the context is actually running. For the very first sound, wait for
  // resume() to finish so it isn't clipped.
  if (ctx.state !== 'running') {
    const r = ctx.resume()
    if (!warmed) {
      const settle = () => { warmed = true; try { doPlay() } catch (_) {} }
      if (r && typeof r.then === 'function') {
        r.then(settle, settle) // handle resolve AND reject so the first sound isn't lost
        return null
      }
      // Legacy webkit: resume() returns no Promise — poll until running (bounded).
      let tries = 0
      const waitReady = () => {
        if (ctx.state === 'running' || tries++ >= 20) { settle(); return }
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(waitReady)
        else setTimeout(waitReady, 16)
      }
      waitReady()
      return null
    }
  }
  warmed = true
  return doPlay()
}

// A short major arpeggio reward flourish — synthesized, needs no asset.
export function playTada() {
  initAudio()
  if (ctx.state !== 'running') ctx.resume()
  const notes = [523.25, 659.25, 783.99, 1046.5] // C5 E5 G5 C6
  const t0 = ctx.currentTime
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.value = freq
    const start = t0 + i * 0.12
    g.gain.setValueAtTime(0.0001, start)
    g.gain.exponentialRampToValueAtTime(0.3, start + 0.03)
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.35)
    osc.connect(g).connect(masterGain)
    osc.start(start)
    osc.stop(start + 0.38)
  })
}

// The "word engine": play a sequence of sound keys back-to-back, slightly
// overlapping, so the phonemes blend into the whole word. Decoded buffers are
// scheduled on the audio clock (smooth); media-element clips are fired with
// timers. Returns { times:[{i,atMs}], totalMs } so the UI can highlight each
// tile as it sounds.
export function playSequence(keys, { overlap = BLEND.overlap, rate = BLEND.rate } = {}) {
  initAudio()
  if (ctx.state !== 'running') ctx.resume()
  const times = []
  let tSec = ctx.currentTime + 0.06
  let elapsedMs = 0
  keys.forEach((key, i) => {
    const e = entries.get(key)
    const ov = overrides.get(key)
    const buf = ov ? ov.buffer : (e && e.buffer)
    const trimSrc = ov ? ov.trim : (e && e.trim)
    times.push({ i, atMs: Math.round(elapsedMs) })
    let stepSec = 0.3
    if (buf) {
      const trim = trimSrc || { start: 0, dur: buf.duration }
      const code = String(key).split(':').pop()
      // A recorded blend clip (bl:<c>_<v>…) is already a whole syllable — play it
      // in full at natural speed; never clip it to a consonant/stop length.
      const isBlend = String(key).startsWith('bl:')
      const isVowel = VOWELS.has(code)
      const r = (isBlend || isVowel) ? BLEND.vowelRate : rate // blends & vowels natural/sustained, consonants snappy
      const outFull = trim.dur / r
      let playedDur = outFull
      if (isBlend) playedDur = outFull                                        // whole recorded syllable, uncut
      else if (STOPS.has(code)) playedDur = Math.min(outFull, BLEND.stopDur)  // quick burst
      else if (!isVowel) playedDur = Math.min(outFull, BLEND.consDur)         // short consonant
      // vowels: keep full length (the stretched nucleus of the syllable)
      const bufDur = Math.min(trim.dur, playedDur * r)
      const src = ctx.createBufferSource()
      src.buffer = buf
      src.playbackRate.value = r
      // Fade each clip in/out so the overlapping ends crossfade smoothly.
      const g = ctx.createGain()
      const fadeIn = Math.min(0.03, playedDur * 0.35)
      const fadeOut = Math.min(0.09, playedDur * 0.5)
      const t0 = tSec, t1 = tSec + playedDur
      g.gain.setValueAtTime(0.0001, t0)
      g.gain.linearRampToValueAtTime(1, t0 + fadeIn)
      g.gain.setValueAtTime(1, Math.max(t0 + fadeIn, t1 - fadeOut))
      g.gain.linearRampToValueAtTime(0.0001, t1)
      src.connect(g).connect(masterGain)
      src.start(tSec, trim.start, bufDur)        // voiced part, stops clipped short
      stepSec = playedDur                        // compressed by rate
    } else if (e && e.media) {
      const el = e.media
      const delay = elapsedMs
      setTimeout(() => { try { el.currentTime = 0; el.playbackRate = rate; const p = el.play(); if (p && p.catch) p.catch(() => {}) } catch (_) {} }, delay)
      stepSec = ((el.duration && isFinite(el.duration)) ? el.duration : 0.34) / rate
    }
    const advSec = Math.max(0.05, stepSec * (1 - overlap)) // overlap into the next sound
    tSec += advSec
    elapsedMs += advSec * 1000
  })
  return { times, totalMs: Math.round(elapsedMs) }
}

// Unlock on the earliest possible touch anywhere, so audio is warm before the
// first letter is crossed (capture phase → runs before component handlers).
if (typeof window !== 'undefined') {
  const kick = () => { try { unlockAudio() } catch (_) {} }
  window.addEventListener('pointerdown', kick, { capture: true })
  window.addEventListener('touchstart', kick, { capture: true })
}
